import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import {
  DollarSign,
  RefreshCw,
  Plus,
  Trash2,
  Edit,
  XCircle,
  AlertCircle,
  Check,
  Calculator,
  FileText,
  Settings,
  Save,
} from 'lucide-react';

interface PricingSettings {
  deliveryFeeMarkup: number;
  mailFeeMarkup: number;
  priceRounding: string;
  defaultMarkupPercent: number;
  defaultMarkupFixed: number;
}

interface PricingRule {
  id: string;
  minPrice: number;
  maxPrice?: number;
  markupPercent: number;
  markupFixed: number;
  conditionType?: string;
  conditionValue?: string;
  priority: number;
  isActive: boolean;
  createdAt: string;
}

interface ConsentText {
  id: string;
  text: string;
  targetKeywords?: string;
  isActive: boolean;
  createdAt: string;
}

type TabType = 'settings' | 'rules' | 'consent' | 'calculator';

export default function PricingSettingsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('settings');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 価格設定
  const [settings, setSettings] = useState<PricingSettings>({
    deliveryFeeMarkup: 100,
    mailFeeMarkup: 0,
    priceRounding: 'none',
    defaultMarkupPercent: 20,
    defaultMarkupFixed: 0,
  });

  // 価格ルール
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);
  const [newRule, setNewRule] = useState({
    minPrice: 0,
    maxPrice: '',
    markupPercent: 20,
    markupFixed: 0,
    conditionType: '',
    conditionValue: '',
    priority: 0,
    isActive: true,
  });

  // 同意確認文
  const [consentTexts, setConsentTexts] = useState<ConsentText[]>([]);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [editingConsent, setEditingConsent] = useState<ConsentText | null>(null);
  const [newConsent, setNewConsent] = useState({
    text: '',
    targetKeywords: '',
    isActive: true,
  });

  // 価格計算テスト
  const [calcInput, setCalcInput] = useState({
    amazonPrice: 3000,
    category: '',
    brand: '',
    title: '',
    deliveryType: 'standard' as 'standard' | 'mail',
  });
  const [calcResult, setCalcResult] = useState<any>(null);

  // 価格設定取得
  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/api/pricing-rules/settings');
      const data = await res.json();
      if (data.settings) {
        setSettings(data.settings);
      }
    } catch (err) {
      setError('価格設定の取得に失敗しました');
    }
  }, []);

  // 価格設定保存
  const saveSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.put('/api/pricing-rules/settings', settings);
      const data = await res.json();
      if (data.success) {
        setSuccessMessage('価格設定を保存しました');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('価格設定の保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 価格ルール取得
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/pricing-rules/rules');
      const data = await res.json();
      setRules(data.rules || []);
    } catch (err) {
      setError('価格ルールの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 価格ルール保存
  const saveRule = async () => {
    if (newRule.minPrice < 0) {
      setError('最低価格は0以上で入力してください');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        minPrice: newRule.minPrice,
        maxPrice: newRule.maxPrice ? parseInt(newRule.maxPrice as any) : undefined,
        markupPercent: newRule.markupPercent,
        markupFixed: newRule.markupFixed,
        conditionType: newRule.conditionType || undefined,
        conditionValue: newRule.conditionValue || undefined,
        priority: newRule.priority,
        isActive: newRule.isActive,
      };

      if (editingRule) {
        await api.put(`/api/pricing-rules/rules/${editingRule.id}`, payload);
      } else {
        await api.post('/api/pricing-rules/rules', payload);
      }
      setShowRuleModal(false);
      setEditingRule(null);
      setNewRule({
        minPrice: 0,
        maxPrice: '',
        markupPercent: 20,
        markupFixed: 0,
        conditionType: '',
        conditionValue: '',
        priority: 0,
        isActive: true,
      });
      await fetchRules();
    } catch (err) {
      setError('価格ルールの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 価格ルール削除
  const deleteRule = async (id: string) => {
    if (!confirm('この価格ルールを削除しますか？')) return;
    try {
      await api.delete(`/api/pricing-rules/rules/${id}`);
      await fetchRules();
    } catch (err) {
      setError('価格ルールの削除に失敗しました');
    }
  };

  // 同意確認文取得
  const fetchConsentTexts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/pricing-rules/consent-texts');
      const data = await res.json();
      setConsentTexts(data.texts || []);
    } catch (err) {
      setError('同意確認文の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 同意確認文保存
  const saveConsent = async () => {
    if (!newConsent.text) {
      setError('テキストを入力してください');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (editingConsent) {
        await api.put(`/api/pricing-rules/consent-texts/${editingConsent.id}`, newConsent);
      } else {
        await api.post('/api/pricing-rules/consent-texts', newConsent);
      }
      setShowConsentModal(false);
      setEditingConsent(null);
      setNewConsent({ text: '', targetKeywords: '', isActive: true });
      await fetchConsentTexts();
    } catch (err) {
      setError('同意確認文の保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 同意確認文削除
  const deleteConsent = async (id: string) => {
    if (!confirm('この同意確認文を削除しますか？')) return;
    try {
      await api.delete(`/api/pricing-rules/consent-texts/${id}`);
      await fetchConsentTexts();
    } catch (err) {
      setError('同意確認文の削除に失敗しました');
    }
  };

  // 価格計算テスト
  const calculatePrice = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/pricing-rules/calculate', calcInput);
      const data = await res.json();
      if (data.success) {
        setCalcResult(data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('価格計算に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (activeTab === 'rules') {
      fetchRules();
    } else if (activeTab === 'consent') {
      fetchConsentTexts();
    }
  }, [activeTab, fetchRules, fetchConsentTexts]);

  return (
    <div className="p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">価格設定・同意確認文</h1>
          <p className="text-sm text-gray-500 mt-1">
            楽天出品時の価格計算ルールと購入時の同意確認文を設定
          </p>
        </div>
      </div>

      {/* メッセージ表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle size={20} />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <Check className="text-green-500" size={20} />
          <span className="text-green-700">{successMessage}</span>
        </div>
      )}

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Settings size={18} />
              基本設定
            </span>
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rules'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <DollarSign size={18} />
              価格ルール
              {rules.filter(r => r.isActive).length > 0 && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                  {rules.filter(r => r.isActive).length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('consent')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'consent'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText size={18} />
              同意確認文
              {consentTexts.filter(t => t.isActive).length > 0 && (
                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                  {consentTexts.filter(t => t.isActive).length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('calculator')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'calculator'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Calculator size={18} />
              価格シミュレーター
            </span>
          </button>
        </nav>
      </div>

      {/* 基本設定タブ */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-6">
            <h3 className="font-medium text-lg">基本価格設定</h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  宅配便追加上乗せ固定値（円）
                </label>
                <input
                  type="number"
                  value={settings.deliveryFeeMarkup}
                  onChange={(e) => setSettings({ ...settings, deliveryFeeMarkup: parseInt(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">宅配便で発送する商品に追加する固定費用</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  メール便追加上乗せ固定値（円）
                </label>
                <input
                  type="number"
                  value={settings.mailFeeMarkup}
                  onChange={(e) => setSettings({ ...settings, mailFeeMarkup: parseInt(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">メール便で発送する商品に追加する固定費用</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  デフォルト上乗せ比率（%）
                </label>
                <input
                  type="number"
                  value={settings.defaultMarkupPercent}
                  onChange={(e) => setSettings({ ...settings, defaultMarkupPercent: parseInt(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">価格ルールにマッチしない場合に適用</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  デフォルト上乗せ固定値（円）
                </label>
                <input
                  type="number"
                  value={settings.defaultMarkupFixed}
                  onChange={(e) => setSettings({ ...settings, defaultMarkupFixed: parseInt(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">価格ルールにマッチしない場合に追加する固定費用</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                価格丸め
              </label>
              <select
                value={settings.priceRounding}
                onChange={(e) => setSettings({ ...settings, priceRounding: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="none">丸めなし</option>
                <option value="10">10円単位で切り上げ</option>
                <option value="100">100円単位で切り上げ</option>
                <option value="1000">1000円単位で切り上げ</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">計算後の価格を指定単位で切り上げ</p>
            </div>

            <button
              onClick={saveSettings}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save size={16} />
              設定を保存
            </button>
          </div>
        </div>
      )}

      {/* 価格ルールタブ */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              仕入れ価格帯ごとに上乗せ比率と固定値を設定できます
            </p>
            <button
              onClick={() => {
                setEditingRule(null);
                setNewRule({
                  minPrice: 0,
                  maxPrice: '',
                  markupPercent: 20,
                  markupFixed: 0,
                  conditionType: '',
                  conditionValue: '',
                  priority: 0,
                  isActive: true,
                });
                setShowRuleModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={16} />
              価格ルール追加
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">仕入価格(円)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">上乗せ比率(%)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">上乗せ固定値(円)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">条件</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : rules.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      価格ルールが登録されていません
                    </td>
                  </tr>
                ) : (
                  rules.map(rule => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        {rule.minPrice.toLocaleString()}
                        {rule.maxPrice ? ` ～ ${rule.maxPrice.toLocaleString()}` : ' 以上'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{rule.markupPercent}%</td>
                      <td className="px-4 py-3 text-sm">{rule.markupFixed.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm">
                        {rule.conditionType && rule.conditionValue ? (
                          <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                            {rule.conditionType}: {rule.conditionValue}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {rule.isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            <Check size={12} />
                            有効
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            <XCircle size={12} />
                            無効
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingRule(rule);
                              setNewRule({
                                minPrice: rule.minPrice,
                                maxPrice: rule.maxPrice?.toString() || '',
                                markupPercent: rule.markupPercent,
                                markupFixed: rule.markupFixed,
                                conditionType: rule.conditionType || '',
                                conditionValue: rule.conditionValue || '',
                                priority: rule.priority,
                                isActive: rule.isActive,
                              });
                              setShowRuleModal(true);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => deleteRule(rule.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 同意確認文タブ */}
      {activeTab === 'consent' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              楽天商品説明に追加する同意確認文を設定できます
            </p>
            <button
              onClick={() => {
                setEditingConsent(null);
                setNewConsent({ text: '', targetKeywords: '', isActive: true });
                setShowConsentModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus size={16} />
              同意確認文追加
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">テキスト</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">使用対象</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : consentTexts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      同意確認文が登録されていません
                    </td>
                  </tr>
                ) : (
                  consentTexts.map(consent => (
                    <tr key={consent.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm max-w-lg">
                        <div className="line-clamp-3">{consent.text}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {consent.targetKeywords ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {consent.targetKeywords}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">全商品</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {consent.isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            <Check size={12} />
                            有効
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            <XCircle size={12} />
                            無効
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingConsent(consent);
                              setNewConsent({
                                text: consent.text,
                                targetKeywords: consent.targetKeywords || '',
                                isActive: consent.isActive,
                              });
                              setShowConsentModal(true);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => deleteConsent(consent.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 価格シミュレータータブ */}
      {activeTab === 'calculator' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-medium text-lg">価格シミュレーター</h3>
            <p className="text-sm text-gray-600">
              設定した価格ルールで実際の販売価格をシミュレートできます
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amazon仕入れ価格（円）
                </label>
                <input
                  type="number"
                  value={calcInput.amazonPrice}
                  onChange={(e) => setCalcInput({ ...calcInput, amazonPrice: parseInt(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  配送タイプ
                </label>
                <select
                  value={calcInput.deliveryType}
                  onChange={(e) => setCalcInput({ ...calcInput, deliveryType: e.target.value as 'standard' | 'mail' })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="standard">宅配便</option>
                  <option value="mail">メール便</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  カテゴリ（任意）
                </label>
                <input
                  type="text"
                  value={calcInput.category}
                  onChange={(e) => setCalcInput({ ...calcInput, category: e.target.value })}
                  placeholder="例: Electronics"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ブランド（任意）
                </label>
                <input
                  type="text"
                  value={calcInput.brand}
                  onChange={(e) => setCalcInput({ ...calcInput, brand: e.target.value })}
                  placeholder="例: TORRAS"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  商品名（任意）
                </label>
                <input
                  type="text"
                  value={calcInput.title}
                  onChange={(e) => setCalcInput({ ...calcInput, title: e.target.value })}
                  placeholder="例: iPhone 16 ケース"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <button
              onClick={calculatePrice}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Calculator size={16} />
              価格を計算
            </button>
          </div>

          {calcResult && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="font-medium text-lg">計算結果</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Amazon仕入れ価格</div>
                  <div className="text-2xl font-bold text-gray-700">¥{calcResult.amazonPrice.toLocaleString()}</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-blue-600">楽天販売価格</div>
                  <div className="text-2xl font-bold text-blue-700">¥{calcResult.rakutenPrice.toLocaleString()}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-green-600">利益額</div>
                  <div className="text-2xl font-bold text-green-700">¥{calcResult.profit.toLocaleString()}</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-purple-600">利益率</div>
                  <div className="text-2xl font-bold text-purple-700">{calcResult.profitPercent}%</div>
                </div>
              </div>

              {calcResult.appliedRule && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-yellow-800 mb-2">適用された価格ルール</div>
                  <div className="text-sm text-yellow-700">
                    価格帯: {calcResult.appliedRule.minPrice.toLocaleString()}円
                    {calcResult.appliedRule.maxPrice ? ` ～ ${calcResult.appliedRule.maxPrice.toLocaleString()}円` : ' 以上'}<br />
                    上乗せ比率: {calcResult.appliedRule.markupPercent}%<br />
                    上乗せ固定値: {calcResult.appliedRule.markupFixed.toLocaleString()}円
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-600">
                <div>配送費上乗せ: ¥{calcResult.settings.deliveryFeeMarkup.toLocaleString()}</div>
                <div>価格丸め: {calcResult.settings.priceRounding === 'none' ? 'なし' : `${calcResult.settings.priceRounding}円単位`}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 価格ルール追加・編集モーダル */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold mb-4">
              {editingRule ? '価格ルール編集' : '価格ルール追加'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">仕入価格（以上）*</label>
                  <input
                    type="number"
                    value={newRule.minPrice}
                    onChange={(e) => setNewRule({ ...newRule, minPrice: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">仕入価格（以下）</label>
                  <input
                    type="number"
                    value={newRule.maxPrice}
                    onChange={(e) => setNewRule({ ...newRule, maxPrice: e.target.value })}
                    placeholder="上限なし"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">上乗せ比率(%)*</label>
                  <input
                    type="number"
                    value={newRule.markupPercent}
                    onChange={(e) => setNewRule({ ...newRule, markupPercent: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">上乗せ固定値(円)</label>
                  <input
                    type="number"
                    value={newRule.markupFixed}
                    onChange={(e) => setNewRule({ ...newRule, markupFixed: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">条件タイプ（任意）</label>
                  <select
                    value={newRule.conditionType}
                    onChange={(e) => setNewRule({ ...newRule, conditionType: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">条件なし</option>
                    <option value="category">カテゴリ</option>
                    <option value="brand">ブランド</option>
                    <option value="keyword">キーワード</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">条件値</label>
                  <input
                    type="text"
                    value={newRule.conditionValue}
                    onChange={(e) => setNewRule({ ...newRule, conditionValue: e.target.value })}
                    placeholder="例: Electronics"
                    disabled={!newRule.conditionType}
                    className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newRule.isActive}
                  onChange={(e) => setNewRule({ ...newRule, isActive: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">有効にする</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRuleModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                onClick={saveRule}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {editingRule ? '更新' : '登録'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 同意確認文追加・編集モーダル */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold mb-4">
              {editingConsent ? '同意確認文編集' : '同意確認文追加'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">テキスト *</label>
                <textarea
                  value={newConsent.text}
                  onChange={(e) => setNewConsent({ ...newConsent, text: e.target.value })}
                  placeholder="例: 配送業者はご指定できません。Amazon個人委託ドライバーの場合がございますので、ご了承ください。"
                  className="w-full border rounded-lg px-3 py-2 h-32"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">使用対象（optional）</label>
                <input
                  type="text"
                  value={newConsent.targetKeywords}
                  onChange={(e) => setNewConsent({ ...newConsent, targetKeywords: e.target.value })}
                  placeholder="スペース区切り。未設定の場合は全商品に適用"
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  いずれかのワードが商品名に含まれる場合にのみ使用します
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newConsent.isActive}
                  onChange={(e) => setNewConsent({ ...newConsent, isActive: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">有効にする</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowConsentModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                onClick={saveConsent}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {editingConsent ? '更新' : '登録'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

